import { FormEvent, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import InputField from '../../../components/Forms/Input/InputField';
import TextAreaField from '../../../components/Forms/Input/TextAreaField';
import FormModal from '../../../components/Modals/FormModal';
import ActionButton from '../../../components/Buttons/ActionButton';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';
import { RestaurantDiningRoomForm } from '../../../types/restaurant/RestaurantDiningRoomTypes';
import ToggleSwitchCard from '../../../components/Forms/Input/ToggleSwitchCard';

interface Props {
  isOpen: boolean;
  form: RestaurantDiningRoomForm;
  saving: boolean;
  onClose: () => void;
  onChange: <K extends keyof RestaurantDiningRoomForm>(
    field: K,
    value: RestaurantDiningRoomForm[K]
  ) => void;
  onSave: () => void;
}

const DiningRoomFormModal = ({
  isOpen,
  form,
  saving,
  onClose,
  onChange,
  onSave,
}: Props) => {
  const errors = useMemo(() => {
    const next: Record<string, string> = {};

    if (!form.name.trim()) {
      next.name = 'El nombre del salón es obligatorio.';
    } else if (form.name.trim().length > 100) {
      next.name = 'El nombre no puede superar 100 caracteres.';
    }

    if (form.description.length > 300) {
      next.description = 'La descripción no puede superar 300 caracteres.';
    }

    if (form.displayOrder < 0) {
      next.displayOrder = 'El orden no puede ser negativo.';
    }

    return next;
  }, [form]);

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (!Object.keys(errors).length) {
      onSave();
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title={form.id ? 'Editar salón' : 'Nuevo salón'}
      maxWidth="max-w-2xl"
      headerExtra={<RestaurantFormHelp topic="diningRoom" />}
    >
      <form onSubmit={submit}>
        <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
          <InputField
            label="Nombre del salón"
            name="name"
            type="text"
            iconKey="store"
            value={form.name}
            required
            error={errors.name}
            placeholder="Ej. Terraza"
            onChange={(event) => onChange('name', event.target.value)}
          />

          <InputField
            label="Orden de visualización"
            name="displayOrder"
            type="number"
            iconKey="list"
            value={form.displayOrder}
            min={0}
            error={errors.displayOrder}
            onChange={(event) =>
              onChange('displayOrder', Math.max(0, Number(event.target.value)))
            }
          />

          {form.id && (
            <ToggleSwitchCard
              label="Registro activo"
              name="diningRoomActive"
              checked={form.active ?? true}
              onChange={() => onChange('active', !(form.active ?? true))}
              description="Reactive el salón para volver a utilizarlo en la distribución de mesas."
              className="mb-5 md:col-span-2"
            />
          )}
        </div>

        <TextAreaField
          label="Descripción"
          name="description"
          iconKey="file"
          value={form.description}
          rows={4}
          error={errors.description}
          placeholder="Ubicación, ambiente o indicaciones para el personal..."
          onChange={(event) => onChange('description', event.target.value)}
        />

        <div className="-mt-3 mb-5 flex justify-end text-xs text-bodydark2">
          {form.description.length}/300
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-stroke pt-5 dark:border-strokedark sm:flex-row sm:justify-end">
          <ActionButton
            label="Cancelar"
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled={saving}
            onClick={onClose}
          />

          <ActionButton
            label={
              saving
                ? 'Guardando...'
                : form.id
                ? 'Guardar cambios'
                : 'Crear salón'
            }
            variant="primary"
            size="md"
            fullWidth={false}
            disabled={saving || !!Object.keys(errors).length}
            icon={
              saving ? <Loader2 className="h-4 w-4 animate-spin" /> : undefined
            }
            onClick={onSave}
            className="min-w-36"
          />
        </div>
      </form>
    </FormModal>
  );
};

export default DiningRoomFormModal;
